import { Icon } from '@mirai-kids-monte/core/src/components/dataDisplay/Icon';
import ExportedImage from 'next-image-export-optimizer';
import { useEffect, useState } from 'react';
import { BLOG_COURSES, type BlogCourseId, type BlogCourseStep } from '../../../constants/BlogCourses';
import { getBlogThumbnailBySlug, getBlogTitleBySlug } from '../../../constants/BlogsMetadata';
import { PageUrl } from '../../../constants/PageUrl';
import styles from './index.module.scss';
import { BLOG_COURSE_PROGRESS_STORAGE_KEY, markStepAsRead, readCourseProgress } from './progress';

type Props = {
  courseId: BlogCourseId;
  currentSlug: string;
};

const buildArticleHref = (slug: string) => `${PageUrl.BLOG_ROOT}${slug}/`;
const otherCoursesHref = PageUrl.BLOG_COURSES;

// Array.prototype.at() はブラウザ互換性のため使わず、範囲外アクセスを型上も undefined として扱うためのヘルパー
const stepAt = (steps: BlogCourseStep[], index: number): BlogCourseStep | undefined => steps[index];

const getStepDotClassName = (isCurrent: boolean, isRead: boolean) => {
  if (isCurrent) {
    return styles['step-dot-current'];
  }
  if (isRead) {
    return styles['step-dot-read'];
  }
  return styles['step-dot'];
};

type PreviewCardProps = {
  href: string;
  eyebrow: string;
  title: string;
  description: string;
  thumbnailSlug: string;
  testId?: string;
  onClick?: () => void;
};

const CourseStepPreviewCard = ({
  href,
  eyebrow,
  title,
  description,
  thumbnailSlug,
  testId,
  onClick,
}: PreviewCardProps) => {
  const thumbnailUrl = getBlogThumbnailBySlug(thumbnailSlug);

  return (
    <a className={styles['preview-card']} href={href} data-testid={testId} onClick={onClick}>
      {thumbnailUrl ? (
        <div className={styles['preview-image-wrapper']}>
          <ExportedImage
            src={thumbnailUrl}
            alt={title}
            className={styles['preview-image']}
            width={160}
            height={120}
            loading="lazy"
          />
        </div>
      ) : null}
      <div className={styles['preview-text']}>
        <span className={styles['preview-eyebrow']}>{eyebrow}</span>
        <span className={styles['preview-title']}>{title}</span>
        <span className={styles['preview-description']}>{description}</span>
      </div>
      <span className={styles['preview-chevron']}>
        <Icon name="ChevronRight" size={16} />
      </span>
    </a>
  );
};

type CompletionBlockProps = {
  courseName: string;
  nextCourse?: { name: string; description: string; steps: BlogCourseStep[] };
  nextCourseReadSlugs: Set<string>;
};

const CourseCompletionBlock = ({ courseName, nextCourse, nextCourseReadSlugs }: CompletionBlockProps) => {
  const firstUnreadStepIndex = nextCourse?.steps.findIndex((step) => !nextCourseReadSlugs.has(step.slug)) ?? -1;
  const isNextCourseComplete = Boolean(nextCourse && firstUnreadStepIndex === -1);
  const nextCourseStepIndex = firstUnreadStepIndex === -1 ? 0 : firstUnreadStepIndex;
  const nextCourseStep = nextCourse ? stepAt(nextCourse.steps, nextCourseStepIndex) : undefined;
  const hasNextCourse = Boolean(nextCourse && nextCourseStep);
  const eyebrow = isNextCourseComplete
    ? '次のコース · 読み終えました'
    : nextCourseStepIndex > 0 && nextCourse
    ? `次のコースを続きから · STEP ${nextCourseStepIndex + 1}/${nextCourse.steps.length}`
    : `次のコース · 全${nextCourse?.steps.length ?? 0}本`;

  return (
    <div className={styles['complete-block']}>
      <p className={styles['complete-message']}>{courseName}を読み終えました。お疲れさまでした！</p>
      {hasNextCourse && nextCourse && nextCourseStep ? (
        <CourseStepPreviewCard
          href={buildArticleHref(nextCourseStep.slug)}
          eyebrow={eyebrow}
          title={isNextCourseComplete ? `${nextCourse.name}をもう一度読む` : `${nextCourse.name}へ進む`}
          description={nextCourse.description}
          thumbnailSlug={nextCourseStep.slug}
        />
      ) : null}
    </div>
  );
};

type StepItemProps = {
  step: BlogCourseStep;
  stepNumber: number;
  isCurrent: boolean;
  isRead: boolean;
  isLast: boolean;
};

const BlogCourseStepItem = ({ step, stepNumber, isCurrent, isRead, isLast }: StepItemProps) => {
  const dotClassName = getStepDotClassName(isCurrent, isRead);
  const dotContent = isRead ? <Icon name="CheckBold" size={10} /> : stepNumber;
  const title = getBlogTitleBySlug(step.slug) ?? '';
  const titleNode = isCurrent ? (
    <span className={styles['step-title-current']} aria-current="step">
      {title}
    </span>
  ) : (
    <a className={styles['step-link']} href={buildArticleHref(step.slug)}>
      {title}
    </a>
  );

  return (
    <li className={styles['rail-item']}>
      <span className={styles['rail-marker']}>
        <span className={dotClassName}>{dotContent}</span>
        {isLast ? null : <span className={styles['rail-line']} />}
      </span>
      {titleNode}
    </li>
  );
};

export const BlogCourseNav = ({ courseId, currentSlug }: Props) => {
  const course = BLOG_COURSES[courseId];
  const { nextCourseId } = course;
  const nextCourse = nextCourseId ? BLOG_COURSES[nextCourseId] : undefined;
  const currentIndex = course.steps.findIndex((step) => step.slug === currentSlug);
  const [readSlugs, setReadSlugs] = useState<Set<string>>(new Set());
  const [nextCourseReadSlugs, setNextCourseReadSlugs] = useState<Set<string>>(new Set());
  const [hasLoadedProgress, setHasLoadedProgress] = useState(false);

  useEffect(() => {
    if (currentIndex === -1) {
      return;
    }

    const syncProgress = () => {
      const { progress: nextProgress, wasMarkedAsRead } = markStepAsRead(courseId, currentSlug);
      const updatedReadSlugs = new Set(nextProgress[courseId] ?? []);
      const persistedProgress = readCourseProgress();
      const persistedReadSlugs = new Set(persistedProgress[courseId] ?? []);
      const isCourseComplete = course.steps.every((step) => updatedReadSlugs.has(step.slug));
      const isPersistedCourseComplete = course.steps.every((step) => persistedReadSlugs.has(step.slug));
      if (wasMarkedAsRead && isCourseComplete && isPersistedCourseComplete) {
        window.dataLayer = window.dataLayer ?? [];
        window.dataLayer.push({
          event: 'blog_course_complete',
          course_id: courseId,
          step: currentIndex + 1,
          article_slug: currentSlug,
        });
      }
      setReadSlugs(updatedReadSlugs);
      setNextCourseReadSlugs(new Set(nextCourseId ? nextProgress[nextCourseId] ?? [] : []));
      setHasLoadedProgress(true);
    };
    const handleStorage = (event: StorageEvent) => {
      if (event.key === BLOG_COURSE_PROGRESS_STORAGE_KEY || event.key === null) {
        syncProgress();
      }
    };

    syncProgress();
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  }, [course.steps, courseId, currentIndex, currentSlug, nextCourseId]);

  if (currentIndex === -1) {
    return null;
  }

  const currentStepNumber = currentIndex + 1;
  const totalSteps = course.steps.length;
  const nextStep = stepAt(course.steps, currentIndex + 1);
  const firstUnreadStepIndex = course.steps.findIndex((step) => !readSlugs.has(step.slug));
  const nextUnreadStepIndex = course.steps.findIndex(
    (step, index) => index > currentIndex && !readSlugs.has(step.slug)
  );
  const isCourseComplete = firstUnreadStepIndex === -1;
  const recommendedStepIndex = nextUnreadStepIndex === -1 ? firstUnreadStepIndex : nextUnreadStepIndex;
  const recommendedStep = hasLoadedProgress
    ? isCourseComplete
      ? undefined
      : stepAt(course.steps, recommendedStepIndex)
    : nextStep;
  const displayedStepIndex = hasLoadedProgress ? recommendedStepIndex : currentIndex + 1;
  const isForwardRecommendation = displayedStepIndex > currentIndex;
  const courseThumbnailUrl = getBlogThumbnailBySlug(course.steps[0].slug);

  const handleNextClick = () => {
    window.dataLayer = window.dataLayer ?? [];
    window.dataLayer.push({
      event: 'blog_course_next_click',
      course_id: courseId,
      step: currentStepNumber,
      article_slug: currentSlug,
    });
  };

  return (
    <div className={styles.base} data-testid="blog-course-nav">
      <div className={styles['caption-wrapper']}>
        <Icon name="Book" size={10} />
        <span className={styles['caption-text']}>テーマ別・連続コース</span>
      </div>

      {/* 1. このコースの情報（コース一覧カードと同じ「画像を上・テキストを下」の縦積み構成で統一） */}
      <div className={styles['course-card']}>
        {courseThumbnailUrl ? (
          <div className={styles['course-image-wrapper']}>
            <ExportedImage
              src={courseThumbnailUrl}
              alt={course.name}
              className={styles['course-image']}
              width={320}
              height={180}
              loading="lazy"
            />
            <span className={styles['course-total-badge']}>全{totalSteps}本</span>
          </div>
        ) : null}
        <div className={styles['course-text']}>
          <span className={styles['course-kicker']}>{course.kicker}</span>
          <span className={styles['course-name']}>{course.name}</span>
          <p className={styles['course-description']}>{course.description}</p>
        </div>
      </div>

      {/* 2. 次の記事へのリンク */}
      {recommendedStep ? (
        <CourseStepPreviewCard
          href={buildArticleHref(recommendedStep.slug)}
          eyebrow={`${isForwardRecommendation ? '次に読む' : '未読の記事から読む'} · STEP ${
            displayedStepIndex + 1
          }/${totalSteps}`}
          title={getBlogTitleBySlug(recommendedStep.slug) ?? ''}
          description={recommendedStep.bridgeCopy}
          thumbnailSlug={recommendedStep.slug}
          testId="next-step-link"
          onClick={handleNextClick}
        />
      ) : hasLoadedProgress && isCourseComplete ? (
        <CourseCompletionBlock
          courseName={course.name}
          nextCourse={nextCourse}
          nextCourseReadSlugs={nextCourseReadSlugs}
        />
      ) : null}

      {/* 3. コースの記事一覧 */}
      <ol className={styles['rail-list']}>
        {course.steps.map((step, index) => (
          <BlogCourseStepItem
            key={step.slug}
            step={step}
            stepNumber={index + 1}
            isCurrent={index === currentIndex}
            isRead={index !== currentIndex && readSlugs.has(step.slug)}
            isLast={index === course.steps.length - 1}
          />
        ))}
      </ol>

      {/* 4. コース一覧に戻る */}
      <a className={styles['other-courses-link']} href={otherCoursesHref}>
        他のコースを見る
        <Icon name="ChevronRight" size={12} />
      </a>
    </div>
  );
};
